import Switch from "react-switch";
import { useState, useRef } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useDatabase } from '../../contexts/DatabaseContext';
import { Button } from '../primitives';
import { catchErrorParse } from '../../ErrorUtility';
import { LIMITS } from '../../Limits';
import Modal from "../portals/Modal";
import VerifyNewEmail from "../popup/VerifyNewEmail";
export default function ContactModuleSettings({
    config,
    workspaceId,
    workspaceType,
    post,
    url,
    workspace,
    setConfig,
    contactModuleSettings,
    setContactModuleSettings,
    recaptchaPrivateKey, 
    setRecaptchaPrivateKey,
    hasChangesContactModule,
    requestedVisualTrigger,
}) {

    const [buttonText, setButtonText] = useState('Update Settings');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({
        title: null,
        inboxEmail: null,
        displayEmail: null,
        phoneNumber: null,
        address: null,
        recaptchaPublicKey: null,
        recaptchaPrivateKey: null
    });
    const { updateWorkspaceConfigDoc, clientData, createContactModuleActionEvent } = useDatabase();
    const t = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const sentEmailLock = useRef(true);

    function validateTextInput(field, value) {
        switch (field) {
            case 'title':
                return value.length <= LIMITS[field];
            case 'inboxEmail':
                return value.length <= LIMITS[field] && value.length > 0;
            case 'displayEmail':
                return !contactModuleSettings.displayEmailEnabled || (value.length <= LIMITS[field] && value.length > 0);
            case 'phoneNumber':
                return !contactModuleSettings.phoneNumberEnabled || (value.length <= LIMITS[field] && value.length > 0);
            case 'address':
                return !contactModuleSettings.addressEnabled || (value.length <= LIMITS[field] && value.length > 0);
            case 'recaptchaPublicKey':
                return !contactModuleSettings.enableRecaptcha || (value.length <= LIMITS[field] && value.length > 0);
            case 'recaptchaPrivateKey':
                return !contactModuleSettings.enableRecaptcha || (value.length <= LIMITS[field] && value.length > 0);
            default:
                return true;
        }
    }

    function isValidEmail(value) {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(value);
    }

    function isValidPhoneNumber(value) {
        const phoneRegex = /^[+\-()0-9\s]{1,20}$/;
        return phoneRegex.test(value);
    }

    const handleSubmit = async () => {
        try {
            setLoading(true);
            let recaptchaPrivateKeyChanged = false;
            if (recaptchaPrivateKey && contactModuleSettings.enableRecaptcha && recaptchaPrivateKey !== workspace.recaptchaPrivateKey) {
                const [result, error, errorCode, errorMessage] = await catchErrorParse(post, `${url}/email/recaptcha`, {
                    workspace: workspaceId,
                    recaptchaPrivateKey,
                });
                if (error) {
                    console.error('Recaptcha Error:', errorMessage);
                    window.dispatchEvent(new CustomEvent('masterError', {
                        detail: {
                            errorCode,
                            message: errorMessage || error.message,
                            action: "recaptcha",
                        }
                    }));
                    return;
                }
                recaptchaPrivateKeyChanged = true;
            }
            if (contactModuleSettings.inboxEmail && !isValidEmail(contactModuleSettings.inboxEmail)) {
                setErrors(prev => ({ ...prev, inboxEmail: t('Invalid email') }));
                return;
            } else {
                setErrors(prev => ({ ...prev, inboxEmail: null }));
            }
            if (contactModuleSettings.displayEmailEnabled && !isValidEmail(contactModuleSettings.displayEmail)) {
                setErrors(prev => ({ ...prev, displayEmail: t('Invalid email') }));
                return;
            } else {
                setErrors(prev => ({ ...prev, displayEmail: null }));
            }
            if (contactModuleSettings.phoneNumberEnabled && !isValidPhoneNumber(contactModuleSettings.phoneNumber)) {
                setErrors(prev => ({ ...prev, phoneNumber: t('Invalid phone number') }));
                return;
            } else {
                setErrors(prev => ({ ...prev, phoneNumber: null }));
            }
            createContactModuleActionEvent(workspaceType, workspaceId,
                contactModuleSettings.title !== config.contactModuleSettings.title,
                contactModuleSettings.inboxEmail !== config.contactModuleSettings.inboxEmail,
                contactModuleSettings.displayEmailEnabled,
                contactModuleSettings.phoneNumberEnabled,
                contactModuleSettings.addressEnabled,
                contactModuleSettings.enableYouGotMailMessage,
                contactModuleSettings.enableRecaptcha,
                contactModuleSettings.recaptchaPublicKey !== config.contactModuleSettings.recaptchaPublicKey,
                recaptchaPrivateKeyChanged,
            );
            await updateWorkspaceConfigDoc(workspaceId, { contactModuleSettings });
            setConfig(prev => ({ ...prev, contactModuleSettings }));
        } catch (error) {
            console.error(error);
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    message: "An error occurred while updating the workspace config.",
                    action: "contact_module",
                }
            }));
        } finally {
            setLoading(false);
            setButtonText('Published!');
            setTimeout(() => {
                setButtonText('Update Settings');
            }, 2000);
        }
    };

    const handleChange = (field, value) => {
        if (field === 'title' || 
            (field === 'displayEmail' && contactModuleSettings.displayEmailEnabled) || 
            (field === 'phoneNumber' && contactModuleSettings.phoneNumberEnabled) || 
            (field === 'address' && contactModuleSettings.addressEnabled))
        {
            requestedVisualTrigger.current = 'contact';
        }
        if (field === 'recaptchaPrivateKey') {
            setRecaptchaPrivateKey(value);
        } else {
            setContactModuleSettings(prev => ({ ...prev, [field]: value }));
        }
        
        if (validateTextInput(field, value)) {
            setErrors(prev => ({ ...prev, [field]: null }));
        } else {
            setErrors(prev => ({ ...prev, [field]: t(LIMITS[`${field}Error`]) }));
        }
    };

    function hasChangesInboxEmail() {
        return contactModuleSettings.inboxEmail !== config.contactModuleSettings.inboxEmail 
            && contactModuleSettings.inboxEmail !== clientData?.email
            && (clientData?.inboxEmails?.length === 0 || !clientData?.inboxEmails?.includes(contactModuleSettings.inboxEmail));
    }

    async function handleClick() {
        if (hasChangesInboxEmail()) {
            setIsOpen(true);
        } else {
            await handleSubmit();
        }
    }

    function hasError() {
        return Object.values(errors).some(error => error !== null && !error.startsWith("Invalid"));
    }

    return (
        <>
        <div className="settings-container-outer">
            <div className="input-settings-group">
                <div className="input-settings-group-inner">
                    <div>
                        <label htmlFor="title" className="settings-input-label">Title:</label>
                        <p className="settings-input-description">This is the title of the contact module that will be displayed to the user.</p>
                    </div>
                    <input
                        type="text"
                        className={`settings-input ${errors?.title ? 'error' : ''}`}
                        id="title"
                        value={contactModuleSettings?.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        placeholder="Example Title"
                    />
                    {errors?.title && <p className="settings-input-error">{errors.title}</p>}
                </div>
            </div>

            <div className="input-settings-group">
                <div className="input-settings-group-inner">
                    <div>
                        <label htmlFor="inboxEmail" className="settings-input-label">Inbox Email:</label>
                        <p className="settings-input-description">This is the email that will be used to receive messages.</p>
                    </div>
                    <input
                        type="email"
                        className={`settings-input ${errors?.inboxEmail ? 'error' : ''}`}
                        id="inboxEmail"
                        value={contactModuleSettings?.inboxEmail}
                        onChange={(e) => handleChange('inboxEmail', e.target.value)}
                        placeholder="inbox@example.com"
                    />
                    {errors?.inboxEmail && <p className="settings-input-error">{errors.inboxEmail}</p>}
                </div>
            </div>

            <div className="input-settings-group">
                <div className="input-settings-group-inner">
                    <div className="input-settings-group-inner-row space-between">
                        <div>
                            <label htmlFor="displayEmail" className="settings-input-label">Display Email:</label>
                            <p className="settings-input-description">This is the email that will be displayed to the user.</p>
                        </div>
                        <Switch
                            id="displayEmailEnabled"
                            checked={contactModuleSettings?.displayEmailEnabled}
                            onChange={(checked) => handleChange('displayEmailEnabled', checked)}
                        />
                    </div>
                    <input
                        type="email"
                        className={`settings-input ${errors?.displayEmail ? 'error' : ''}`}
                        id="displayEmail"
                        value={contactModuleSettings?.displayEmail}
                        onChange={(e) => handleChange('displayEmail', e.target.value)}
                        placeholder="display@example.com"
                    />
                    {errors?.displayEmail && <p className="settings-input-error">{errors.displayEmail}</p>}
                </div>
            </div>

            <div className="input-settings-group">
                <div className="input-settings-group-inner">
                    <div className="input-settings-group-inner-row space-between">
                        <div>
                            <label htmlFor="phoneNumber" className="settings-input-label">Phone Number:</label>
                            <p className="settings-input-description">This is the phone number that will be displayed to the user.</p>
                        </div>
                        <Switch
                            id="phoneNumberEnabled"
                            checked={contactModuleSettings?.phoneNumberEnabled}
                            onChange={(checked) => handleChange('phoneNumberEnabled', checked)}
                        />
                    </div>
                    <input
                        type="tel"
                        className={`settings-input ${errors?.phoneNumber ? 'error' : ''}`}
                        id="phoneNumber"
                        value={contactModuleSettings?.phoneNumber}
                        onChange={(e) => handleChange('phoneNumber', e.target.value)}
                        placeholder="+1 (123) 456-7890"
                    />
                    {errors?.phoneNumber && <p className="settings-input-error">{errors.phoneNumber}</p>}
                </div>
            </div>

            <div className="input-settings-group">
                <div className="input-settings-group-inner">
                    <div className="input-settings-group-inner-row space-between">
                        <div>
                            <label htmlFor="address" className="settings-input-label">Address:</label>
                            <p className="settings-input-description">This is the address that will be displayed to the user.</p>
                        </div>
                        <Switch
                            id="addressEnabled"
                            checked={contactModuleSettings?.addressEnabled}
                            onChange={(checked) => handleChange('addressEnabled', checked)}
                        />
                    </div>
                    <input
                        type="text"
                        className={`settings-input ${errors?.address ? 'error' : ''}`}
                        id="address"
                        value={contactModuleSettings?.address}
                        onChange={(e) => handleChange('address', e.target.value)}
                        placeholder="123 Main St, City, State, Zip"
                    />
                    {errors?.address && <p className="settings-input-error">{errors.address}</p>}
                </div>
            </div>

            <div className="input-settings-group">
                <div className="input-settings-group-inner-row">
                    <Switch
                        id="enableYouGotMailMessage"
                        checked={contactModuleSettings?.enableYouGotMailMessage}
                        onChange={(checked) => handleChange('enableYouGotMailMessage', checked)}
                    />
                    <label htmlFor="enableYouGotMailMessage" className="settings-input-label">Enable Email Notification</label>
                </div>
            </div>
            
            <div className="input-settings-group">
                <div className="input-settings-group-inner-row">
                    <Switch
                        id="enableRecaptcha"
                        checked={contactModuleSettings?.enableRecaptcha}
                        onChange={(checked) => handleChange('enableRecaptcha', checked)}
                    />
                    <label htmlFor="enableRecaptcha" className="settings-input-label">Enable Recaptcha</label>
                </div>
            </div>
            {/* https://www.google.com/recaptcha/admin/create */}
            {contactModuleSettings.enableRecaptcha && (
                <div className="input-settings-group">
                    <div className="input-settings-group-inner">
                        <label htmlFor="recaptchaPublicKey" className="settings-input-label">Site Key:</label>
                        <p className="settings-input-description">This is the public key that will be used to verify the recaptcha.</p>
                        <input
                            type="text"
                            className={`settings-input ${errors?.recaptchaPublicKey ? 'error' : ''}`}
                            id="recaptchaPublicKey"
                            value={contactModuleSettings.recaptchaPublicKey}
                            onChange={(e) => handleChange('recaptchaPublicKey', e.target.value)}
                            placeholder="Recaptcha Public Key"
                        />
                        {errors?.recaptchaPublicKey && <p className="settings-input-error">{errors.recaptchaPublicKey}</p>}
                    </div>
                </div>
            )}

            {contactModuleSettings.enableRecaptcha && (
                <div className="input-settings-group">
                    <div className="input-settings-group-inner">
                        <label htmlFor="recaptchaPrivateKey" className="settings-input-label">Secret Key:</label>
                        <p className="settings-input-description">This is the private key that will be used to verify the recaptcha.</p>
                        <input
                            type="password"
                            className={`settings-input ${errors?.recaptchaPrivateKey ? 'error' : ''}`}
                            id="recaptchaPrivateKey"
                            value={recaptchaPrivateKey}
                            onChange={(e) => handleChange('recaptchaPrivateKey', e.target.value)}
                            placeholder="Recaptcha Private Key"
                        />
                        {errors?.recaptchaPrivateKey && <p className="settings-input-error">{errors.recaptchaPrivateKey}</p>}
                    </div>
                </div>
            )}
            <Button className="submit settings" 
                onClick={handleClick}
                loading={loading} 
                loadingFinish={buttonText === 'Published!'}
                disabled={!hasChangesContactModule || hasError()}
            >
                {buttonText}
            </Button>
        </div>
        <Modal open={isOpen} onClickOutside={() => setIsOpen(false)} lock={true}>
            <VerifyNewEmail 
                setIsOpen={setIsOpen}
                email={contactModuleSettings.inboxEmail}
                loading={loading}
                setLoading={setLoading}
                isInboxEmail={true}
                sentEmailLock={sentEmailLock}
                onSuccess={handleSubmit}
            />
        </Modal>
        </>
    );
}
